import React, { memo, useEffect, useMemo, useState } from "react"

import { v4 as uuidv4 } from "uuid"

import { Plugin } from "@fuse/recoil/plugins"
import { UserRole } from "@fuse/recoil/pluginStudio/types"

import { pluginComponents } from "@fuse/utils/pluginComponents"
import { pluginSandbox } from "@fuse/utils/pluginSandbox"
import { pluginStyles } from "@fuse/utils/pluginStyles"

import {
  bindPluginApi,
  BoundPluginApi,
  productionRawApi,
  RawPluginApi,
} from "../api/pluginDataApi"

/**
 * Event metadata for the plugin
 */
interface EventMetadata {
  name?: string
  description?: string
  timing?: {
    eventStartTime?: string
    eventEndTime?: string
  }
  location?: {
    eventLocation?: string
  }
  flyerImage?: string
}

/**
 * Context data passed to the plugin sandbox
 */
interface PluginContextData {
  eventId: string
  userId: string
  userRole: string
  settings?: {
    triggerDelay?: number
    simulateNetworkDelay?: boolean
  }
}

/**
 * Props for the PluginRenderer component
 */
interface PluginRendererProps {
  plugin: Plugin
  config: {
    configuration?: Record<string, unknown>
    [key: string]: unknown
  }
  eventId: string
  userId: string
  userRole: UserRole
  eventMetadata?: EventMetadata
  isLoggedIn: boolean
  isStudioMode?: boolean
  studioApi?: RawPluginApi
  instanceKey?: string
  pluginComponents?: Record<string, React.ComponentType<any>>
  pluginStyles?: Record<string, string>
  renderPluginHeader?: () => React.ReactElement | null
}

/**
 * Map user roles to plugin code sources
 */
const roleToCodeMap: Record<UserRole, (plugin: Plugin) => string | undefined> =
  {
    [UserRole.HOST_CONFIG_VIEW]: (plugin: Plugin) =>
      plugin.code?.transpiledFrontendHostConfigCodeWeb ||
      plugin.code?.transpiledFrontendHostViewCodeWeb ||
      plugin.code?.transpiledFrontendGuestCodeWeb,

    [UserRole.HOST_EVENT_VIEW]: (plugin: Plugin) =>
      plugin.code?.transpiledFrontendHostViewCodeWeb ||
      plugin.code?.transpiledFrontendGuestCodeWeb,

    [UserRole.GUEST]: (plugin: Plugin) =>
      plugin.code?.transpiledFrontendGuestCodeWeb,
  }

/**
 * Component that renders a plugin in a sandbox environment
 */
const PluginRenderer: React.FC<PluginRendererProps> = memo(
  ({
    plugin,
    config,
    eventId,
    userId,
    userRole,
    eventMetadata = {},
    isLoggedIn = true,
    isStudioMode = false,
    studioApi,
    instanceKey = "default",
    pluginComponents,
    pluginStyles,
    renderPluginHeader,
  }) => {
    // Memoize plugin and config to avoid unnecessary re-renders
    const memoizedPlugin = useMemo(() => plugin, [plugin?.id, plugin?.code])
    const memoizedConfig = useMemo(() => config, [JSON.stringify(config)])

    const [error, setError] = useState<string | null>(null)
    const [renderComponent, setRenderComponent] =
      useState<React.ReactNode>(null)

    // Validate configurations
    useEffect(() => {
      const setupPlugin = async () => {
        console.log(
          `[PLUGIN] PluginRenderer.setupPlugin starting for plugin: ${memoizedPlugin?.id}`,
        )

        if (isStudioMode && !studioApi) {
          setError("Studio API is required when in studio mode")
          return
        }

        if (!memoizedPlugin || !memoizedPlugin.code) {
          setError("Plugin code is missing")
          return
        }

        try {
          // Get code based on role using the lookup map
          const getCodeForRole =
            roleToCodeMap[userRole] || roleToCodeMap[UserRole.GUEST]
          const codeToUse = getCodeForRole(memoizedPlugin)

          if (!codeToUse) {
            setError("No compatible code available for this plugin")
            return
          }

          console.log(
            `[PLUGIN] PluginRenderer creating boundApi for plugin: ${memoizedPlugin.id}`,
          )
          // Create bound API from either studio or production raw API
          const boundApi = bindPluginApi(
            isStudioMode && studioApi ? studioApi : productionRawApi,
            eventId,
            memoizedPlugin.id,
            userId,
          )
          console.log(
            `[PLUGIN] PluginRenderer boundApi created for plugin: ${memoizedPlugin.id}`,
          )

          // Create minimal context data needed for the sandbox
          const contextData: PluginContextData = {
            eventId,
            userId,
            userRole,
            settings: {
              triggerDelay: 0,
              simulateNetworkDelay: false,
            },
          }

          console.log(
            `[PLUGIN] PluginRenderer creating sandbox for plugin: ${memoizedPlugin.id}`,
          )
          // Create the sandbox - let the plugin access data through the API
          const sandbox = pluginSandbox(
            codeToUse,
            contextData,
            userRole,
            boundApi,
          )
          console.log(
            `[PLUGIN] PluginRenderer sandbox created for plugin: ${memoizedPlugin.id}`,
          )

          if (sandbox.error) {
            setError(`Failed to create sandbox: ${sandbox.message}`)
            return
          }

          // Create the component props in a consistent way - minimal approach
          const componentProps = {
            key: `${memoizedPlugin?.id || "unknown"}-${userRole}-${instanceKey}-${uuidv4()}`,
            isHostConfigView: userRole === UserRole.HOST_CONFIG_VIEW,
            window,
            React,
            pluginApi: boundApi,
            // Explicitly pass these to the component to ensure they're accessible
            pluginComponents: pluginComponents || {},
            pluginStyles: pluginStyles || {},
            eventMetadata,
            eventId,
            pluginId: memoizedPlugin?.id,
            userId: userId,
          }

          console.log(
            `[PLUGIN] PluginRenderer creating React element for plugin: ${memoizedPlugin.id}`,
          )
          // Create the component
          setRenderComponent(
            React.createElement(sandbox.component, componentProps),
          )
          console.log(
            `[PLUGIN] PluginRenderer React element created for plugin: ${memoizedPlugin.id}`,
          )
        } catch (err) {
          console.error("Error rendering plugin:", err)
          setError(
            `Error rendering plugin: ${err instanceof Error ? err.message : String(err)}`,
          )
        }
      }

      setupPlugin()
    }, [
      memoizedPlugin?.id,
      userRole,
      instanceKey,
      studioApi,
      eventId,
      userId,
      memoizedConfig,
      isStudioMode,
      eventMetadata,
      isLoggedIn,
      pluginComponents,
      pluginStyles,
    ])

    if (error) {
      return (
        <div className="p-4 border border-red-200 rounded-lg bg-red-50">
          <h3 className="text-lg font-medium text-red-800">
            {memoizedPlugin.manifest.name}
          </h3>
          <p className="text-sm text-red-600 mt-2">{error}</p>
        </div>
      )
    }

    if (!renderComponent) {
      return (
        <div className="p-4 bg-white/10 border-white/20 border rounded-2xl">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-white/20 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-white/20 rounded"></div>
                <div className="h-4 bg-white/20 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="bg-white/10 border-white/20 border rounded-2xl w-full h-full overflow-y-auto">
        <div className="plugin-content text-white p-4">
          {renderPluginHeader && renderPluginHeader()}
          {renderComponent}
        </div>
      </div>
    )
  },
)

export default PluginRenderer
